/**
 * useContagemGlobalFilter - Hook para filtro global de cliente na página de Contagem
 *
 * Gerencia o estado do filtro global de cliente que é aplicado em todas as abas
 * exceto "Agendamento". Este filtro aparece no header da página.
 */
import { useState, useMemo, useCallback } from 'react';
import type { FilterOption } from '../components/MultiSelectFilter';

interface Cliente {
  id: number;
  nome_fantasia?: string;
  razao_social?: string;
}

export interface UseContagemGlobalFilterReturn {
  // Estado do filtro
  globalClienteFilter: (string | number)[];
  setGlobalClienteFilter: (ids: (string | number)[]) => void;

  // Opções para o dropdown
  globalClienteOptions: FilterOption[];

  // Flags úteis
  hasGlobalFilter: boolean;

  // Função de filtro genérica por cliente_id
  filterByClienteId: <T extends { cliente_id?: number; clienteId?: number }>(items: T[]) => T[];

  // Função de filtro genérica por nome do cliente
  filterByClienteName: <T extends { cliente?: string; cliente_nome_fantasia?: string }>(items: T[]) => T[];
}

export function useContagemGlobalFilter(jobClientes: Cliente[]): UseContagemGlobalFilterReturn {
  // Estado: Array vazio = "Todos os Clientes"
  const [globalClienteFilter, setGlobalClienteFilter] = useState<(string | number)[]>([]);

  // Construir opções do dropdown
  const globalClienteOptions = useMemo<FilterOption[]>(() => {
    return jobClientes.map(c => ({
      value: c.id,
      label: c.nome_fantasia || c.razao_social || `Cliente ${c.id}`
    }));
  }, [jobClientes]);

  const hasGlobalFilter = globalClienteFilter.length > 0;

  // Filtrar por cliente_id ou clienteId
  const filterByClienteId = useCallback(<T extends { cliente_id?: number; clienteId?: number }>(items: T[]): T[] => {
    if (!hasGlobalFilter) return items;
    return items.filter(item => {
      const clienteId = item.cliente_id || item.clienteId;
      return clienteId && globalClienteFilter.includes(clienteId);
    });
  }, [globalClienteFilter, hasGlobalFilter]);

  // Filtrar por nome do cliente (para dados que não têm cliente_id direto)
  const filterByClienteName = useCallback(<T extends { cliente?: string; cliente_nome_fantasia?: string }>(items: T[]): T[] => {
    if (!hasGlobalFilter) return items;

    // Mapear IDs selecionados para nomes
    const selectedNames = new Set<string>();
    globalClienteFilter.forEach(id => {
      const cliente = jobClientes.find(c => c.id === Number(id));
      if (cliente) {
        if (cliente.nome_fantasia) selectedNames.add(cliente.nome_fantasia);
        if (cliente.razao_social) selectedNames.add(cliente.razao_social);
      }
    });

    return items.filter(item => {
      const nome = item.cliente || item.cliente_nome_fantasia;
      return nome && selectedNames.has(nome);
    });
  }, [globalClienteFilter, hasGlobalFilter, jobClientes]);

  return {
    globalClienteFilter,
    setGlobalClienteFilter,
    globalClienteOptions,
    hasGlobalFilter,
    filterByClienteId,
    filterByClienteName,
  };
}

export default useContagemGlobalFilter;
